import type { RelayMetadata } from '@/contexts/AppContext';

/** Normalize a relay or Blossom URL for deduplication. */
export function normalizeNetworkUrl(url: string): string {
  return url.trim().toLowerCase().replace(/\/+$/, '');
}

/**
 * App default relays used as bootstrap / fallback when the user has no
 * configured list yet (cold start, logged out, empty NIP-65).
 */
export const APP_RELAYS: RelayMetadata = {
  relays: [
    { url: 'wss://relay.ditto.pub', read: true, write: true },
    { url: 'wss://relay.primal.net', read: true, write: true },
    { url: 'wss://relay.damus.io', read: true, write: true },
    { url: 'wss://nos.lol', read: true, write: false },
  ],
  updatedAt: 0,
};

export type RelayEntry = RelayMetadata['relays'][number];

/**
 * Active connection pool: the user's configured list exclusively when
 * non-empty; otherwise app defaults so the client can still bootstrap
 * (login, first NIP-65 fetch, profiles).
 */
export function getEffectiveRelays(configured: RelayMetadata): RelayMetadata {
  const usable = configured.relays.filter((r) => r.url && (r.read || r.write));
  if (usable.length > 0) {
    return {
      relays: dedupeRelays(usable),
      updatedAt: configured.updatedAt,
    };
  }
  return {
    relays: [...APP_RELAYS.relays],
    updatedAt: configured.updatedAt,
  };
}

/** True when effective pool is only the app bootstrap set (no user list). */
export function isUsingAppRelayFallback(configured: RelayMetadata): boolean {
  const usable = configured.relays.filter((r) => r.url && (r.read || r.write));
  return usable.length === 0;
}

/**
 * True when local config still matches stock OpenDork bootstrap relays
 * (cold start / never customized). Used for auto-pull and UI copy.
 */
export function isStockAppRelayConfig(configured: RelayMetadata): boolean {
  if (configured.updatedAt !== 0) return false;
  const usable = configured.relays.filter((r) => r.url && (r.read || r.write));
  if (usable.length === 0) return true;
  if (usable.length !== APP_RELAYS.relays.length) return false;
  return usable.every((r) =>
    APP_RELAYS.relays.some(
      (a) =>
        normalizeNetworkUrl(a.url) === normalizeNetworkUrl(r.url) &&
        a.read === r.read &&
        a.write === r.write,
    ),
  );
}

export function dedupeRelays(relays: RelayEntry[]): RelayEntry[] {
  const seen = new Set<string>();
  const out: RelayEntry[] = [];
  for (const r of relays) {
    const key = normalizeNetworkUrl(r.url);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({
      url: r.url.trim().replace(/\/+$/, '') || r.url,
      read: !!r.read,
      write: !!r.write,
    });
  }
  return out;
}

export function dedupeUrls(urls: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of urls) {
    const trimmed = raw.trim().replace(/\/+$/, '');
    if (!trimmed) continue;
    const key = normalizeNetworkUrl(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

/** Coerce user input into a wss/ws relay URL, or null if invalid. */
export function parseRelayUrlInput(raw: string): string | null {
  let s = raw.trim();
  if (!s) return null;
  if (!/^wss?:\/\//i.test(s)) {
    s = `wss://${s}`;
  }
  try {
    const u = new URL(s);
    if (u.protocol !== 'wss:' && u.protocol !== 'ws:') return null;
    if (!u.hostname) return null;
    // Drop trailing slash for consistency
    return s.replace(/\/+$/, '');
  } catch {
    return null;
  }
}

/** Coerce user input into an https Blossom server URL, or null. */
export function parseBlossomUrlInput(raw: string): string | null {
  let s = raw.trim();
  if (!s) return null;
  if (!/^https?:\/\//i.test(s)) {
    s = `https://${s}`;
  }
  try {
    const u = new URL(s);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (!u.hostname) return null;
    return s.replace(/\/+$/, '');
  } catch {
    return null;
  }
}

/** NIP-65 `r` tags from a relay list. */
export function buildNip65Tags(relays: RelayEntry[]): string[][] {
  const tags: string[][] = [];
  for (const r of dedupeRelays(relays)) {
    if (r.read && r.write) {
      tags.push(['r', r.url]);
    } else if (r.read) {
      tags.push(['r', r.url, 'read']);
    } else if (r.write) {
      tags.push(['r', r.url, 'write']);
    }
  }
  return tags;
}

/** BUD-03 / kind 10063 `server` tags (order = preference). */
export function buildBlossomTags(servers: string[]): string[][] {
  return dedupeUrls(servers).map((url) => ['server', url]);
}

export function parseNip65RelaysFromTags(tags: string[][]): RelayEntry[] {
  const out: RelayEntry[] = [];
  const seen = new Set<string>();

  for (const tag of tags) {
    if (tag[0] !== 'r' || !tag[1]) continue;
    const url = tag[1].trim().replace(/\/+$/, '');
    if (!url) continue;
    const key = normalizeNetworkUrl(url);
    if (seen.has(key)) continue;
    seen.add(key);

    const marker = (tag[2] || '').toLowerCase();
    if (marker === 'read') {
      out.push({ url, read: true, write: false });
    } else if (marker === 'write') {
      out.push({ url, read: false, write: true });
    } else {
      out.push({ url, read: true, write: true });
    }
  }

  return out;
}

/**
 * Parse BUD-03 / NIP-B7 Blossom server URLs from kind 10063 tags.
 * Primary tag is `server` (Ditto publishes `["server", url]`).
 * Also accept common variants and trailing-slash URLs from Ditto's normalizer.
 */
export function parseBlossomServersFromTags(tags: string[][]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();

  for (const tag of tags) {
    if (!tag || tag.length < 2) continue;
    const name = String(tag[0] || '').toLowerCase().trim();
    // BUD-03: ["server", "https://…"] — Ditto uses exactly this
    if (name !== 'server' && name !== 'servers' && name !== 'url' && name !== 'u') {
      continue;
    }
    const raw = String(tag[1] || '').trim();
    if (!raw) continue;
    if ((name === 'url' || name === 'u') && !/^https?:\/\//i.test(raw)) continue;

    let url = raw;
    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }
    // Ditto normalizes with trailing slash via URL.toString() — strip for storage
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') continue;
      // Prefer origin only when path is empty or "/"
      if (!parsed.pathname || parsed.pathname === '/') {
        url = parsed.origin;
      } else {
        url = `${parsed.origin}${parsed.pathname}`.replace(/\/+$/, '');
      }
    } catch {
      continue;
    }

    const key = normalizeNetworkUrl(url);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(url);
  }

  return out;
}

/** Compare two relay lists (order-insensitive, normalized). */
export function relaysEqual(a: RelayEntry[], b: RelayEntry[]): boolean {
  const key = (r: RelayEntry) =>
    `${normalizeNetworkUrl(r.url)}|${r.read ? 1 : 0}|${r.write ? 1 : 0}`;
  const sa = [...a].map(key).sort().join('\n');
  const sb = [...b].map(key).sort().join('\n');
  return sa === sb;
}

export function urlsEqual(a: string[], b: string[]): boolean {
  const sa = [...a].map(normalizeNetworkUrl).filter(Boolean).sort().join('\n');
  const sb = [...b].map(normalizeNetworkUrl).filter(Boolean).sort().join('\n');
  return sa === sb;
}
